import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { IoArrowBackCircleOutline, IoAdd, IoClose } from 'react-icons/io5'
import { Button, IconButton, CircularProgress } from '@material-ui/core'
import { toast } from 'react-toastify'

// components
import Layout from '../../components/Layout/Layout'
import CustomMultiSelect from '../../components/CustomMultiSelect/CustomMultiSelect'
import FormInput from '../../components/FormInput/FormInput'

// services
import {
  getIntegratedBusinesses,
  syncCin7Products,
} from '../../services/products'

const Cin7Sync = () => {
  const history = useHistory()
  const [businesses, setBusinesses] = useState([])
  const [selectedBusinesses, setSelectedBusinesses] = useState([])
  const [urlInput, setUrlInput] = useState('')
  const [urls, setUrls] = useState([])
  const [syncing, setSyncing] = useState(false)
  const [syncResults, setSyncResults] = useState(null)

  const fetchBusinesses = async () => {
    try {
      const { data } = await getIntegratedBusinesses()
      setBusinesses(data.businesses || [])
    } catch (error) {
      toast.error('Unable to fetch integrated businesses.')
    }
  }

  useEffect(() => {
    fetchBusinesses()
  }, [])

  const handleAddUrl = () => {
    if (!urlInput.trim()) return
    if (urls.includes(urlInput.trim())) {
      toast.warning('URL already added.')
      return
    }
    setUrls([...urls, urlInput.trim()])
    setUrlInput('')
  }

  const handleRemoveUrl = (urlToRemove) => {
    setUrls(urls.filter((url) => url !== urlToRemove))
  }

  const handleSync = async () => {
    if (urls.length === 0) {
      toast.error('Please add at least one product URL.')
      return
    }
    if (selectedBusinesses.length === 0) {
      toast.error('Please select at least one business.')
      return
    }

    setSyncing(true)
    setSyncResults(null)
    try {
      const payload = {
        urls,
        business_uuids: selectedBusinesses.map((b) => b.uuid),
      }
      const { data } = await syncCin7Products(payload)
      setSyncResults(data)
      toast.success('Sync process completed.')
      setUrls([])
      setSelectedBusinesses([])
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Sync failed. Please try again.',
      )
    } finally {
      setSyncing(false)
    }
  }

  return (
    <Layout>
      <div className="flex flex-col items-center py-10 space-y-5 mb-[20px]">
        <div className="w-[70%] flex items-center space-x-3">
          <IconButton
            onClick={() => history.goBack()}
            disableRipple
            className="p-0 hover:bg-transparent"
          >
            <IoArrowBackCircleOutline className="w-10 h-10 text-gray2" />
          </IconButton>
          <h1 className="font-bold text-[30px] text-gray1 mb-0">
            Cin7 Manual Sync
          </h1>
        </div>

        <div className="bg-white w-[70%] p-5 space-y-5 rounded-md shadow-sm">
          {/* URL Input */}
          <div className="space-y-3">
            <label className="font-medium text-[#3D4356]">Product URLs</label>
            <div className="flex space-x-3">
              <div className="flex-grow">
                <FormInput
                  type="url"
                  placeholder="Enter product URL"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                />
              </div>
              <Button
                variant="contained"
                disabled={!urlInput}
                className="bg-orange text-white capitalize shadow-none"
                onClick={handleAddUrl}
                startIcon={<IoAdd />}
              >
                Add to list
              </Button>
            </div>

            {/* URL List */}
            {urls.length > 0 && (
              <div className="border rounded-md p-3 space-y-2 bg-gray-50">
                {urls.map((url, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center bg-white p-2 rounded border border-gray-200"
                  >
                    <span className="text-sm text-gray-700 truncate">
                      {url}
                    </span>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveUrl(url)}
                      className="text-red-500 hover:bg-red-50"
                    >
                      <IoClose />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Business Selection */}
          <div className="space-y-3">
            <label className="font-medium text-[#3D4356]">
              Select Businesses
            </label>
            <CustomMultiSelect
              options={businesses}
              selections={selectedBusinesses}
              addItem={(item) => {
                const exists = selectedBusinesses.find(
                  (b) => b.uuid === item.uuid,
                )
                if (!exists)
                  setSelectedBusinesses([...selectedBusinesses, item])
              }}
              removeItem={(item) => {
                setSelectedBusinesses(
                  selectedBusinesses.filter((b) => b.uuid !== item.uuid),
                )
              }}
              placeholder="Select businesses"
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-5">
            <Button
              variant="contained"
              className={`capitalize shadow-none ${
                syncing || urls.length === 0 || selectedBusinesses.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-orange text-white'
              }`}
              onClick={handleSync}
              disabled={
                syncing || urls.length === 0 || selectedBusinesses.length === 0
              }
              startIcon={
                syncing && <CircularProgress size={16} color="inherit" />
              }
            >
              {syncing ? 'Syncing...' : 'Sync Cin7 Products'}
            </Button>
          </div>
        </div>

        {/* Results Display */}
        {syncResults && (
          <div className="bg-white w-[70%] p-5 space-y-5 rounded-md shadow-sm">
            <h2 className="font-bold text-xl text-gray1">Sync Results</h2>

            {syncResults.results?.map((res, idx) => (
              <div key={idx} className="border rounded-md p-4 space-y-2">
                <div className="flex justify-between items-center border-b pb-2 mb-2">
                  <h3 className="font-semibold text-lg">{res.business_name}</h3>
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      res.status === 'ok'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {res.status.toUpperCase()}
                  </span>
                </div>

                <div className="space-y-2">
                  {res.products?.map((prod, pIdx) => (
                    <div
                      key={pIdx}
                      className="text-sm border-l-4 pl-3 py-1 flex justify-between items-start bg-gray-50 border-gray-300"
                    >
                      <div>
                        <div className="font-medium">
                          Product ID: {prod.product_id}
                        </div>
                        {prod.result && (
                          <div className="text-gray-600 mt-1">
                            <div>Name: {prod.result.product_name}</div>
                            <div>SKU: {prod.result.sku}</div>
                          </div>
                        )}
                        {prod.error && (
                          <div className="text-red-600 mt-1">
                            Error: {prod.error}
                          </div>
                        )}
                      </div>
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          prod.status === 'success'
                            ? 'bg-green-200 text-green-900'
                            : 'bg-red-200 text-red-900'
                        }`}
                      >
                        {prod.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex justify-end pt-5">
              <Button
                variant="contained"
                className="bg-orange text-white capitalize shadow-none"
                onClick={() => history.push('/products')}
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}

export default Cin7Sync
